import AutoPagination from "@/components/AutoPagination";
import { useDatasetVersionTableContext } from "@/components/DatasetVersionTable/context";
import { Json } from "@/components/Json";
import { Skeleton } from "@/components/Skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/kit/ui/table";
import {
  get_simple_data_type_name,
  parseSchemaField,
} from "@/utils/data_utils_schema";
import { useMediaQuery, useOnChangeEffect } from "@/utils/hooks";
import type { Schema, SchemaField } from "@aqora-io/aqora-data-utils";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { range } from "lodash";
import * as React from "react";
import {
  graphql,
  useFragment,
  usePreloadedQuery,
  useQueryLoader,
  useRefetchableFragment,
  type PreloadedQuery,
} from "react-relay";
import type { DatasetVersionTableFragment$key } from "./__generated__/DatasetVersionTableFragment.graphql";
import type { DatasetVersionTableRecordsFragment$key } from "./__generated__/DatasetVersionTableRecordsFragment.graphql";
import type { DatasetVersionTableRecordsFragmentPaginationQuery as DatasetVersionTableRecordsFragmentPaginationQueryType } from "./__generated__/DatasetVersionTableRecordsFragmentPaginationQuery.graphql";
import RecordsPaginationQuery from "./__generated__/DatasetVersionTableRecordsFragmentPaginationQuery.graphql";
import { DESKTOP_MEDIA_QUERY } from "@/common/constants";

const DEFAULT_PAGE_SIZE = 10;

const Fragment = graphql`
  fragment DatasetVersionTableFragment on DatasetVersion {
    id
    schema
    rows
    ...DatasetVersionEditFileFragment
  }
`;

const RecordsFragment = graphql`
  fragment DatasetVersionTableRecordsFragment on DatasetVersion
  @refetchable(queryName: "DatasetVersionTableRecordsFragmentPaginationQuery")
  @argumentDefinitions(
    limit: { type: "Int", defaultValue: 10 }
    offset: { type: "Int", defaultValue: 0 }
  ) {
    id
    records(limit: $limit, offset: $offset)
  }
`;

function Column({ field }: { field: SchemaField }) {
  return (
    <>
      <p>{field.name}</p>
      <p className="lowercase text-xs text-grey-500">
        {get_simple_data_type_name(field)}
      </p>
    </>
  );
}

const createSchemaColumns = (schema: Schema) => {
  const columnHelper = createColumnHelper<Record<string, unknown>>();
  return schema.fields.map((field, index) =>
    columnHelper.accessor((row) => row[field.name], {
      id: field.name || `column_${index}`,
      header: () => <Column field={field} />,
      cell: (props) => (
        <Json
          value={parseSchemaField(field, props.getValue())}
          settings={[
            {
              showType: "none",
              rawString: true,
            },
            {
              showType: "all",
              rawString: false,
            },
          ]}
        />
      ),
    }),
  );
};

const useDatasetVersionFileTable = (
  datasetVersion: DatasetVersionTableRecordsFragment$key,
  schema: Schema,
  rowCount: number,
  page: number,
  pageSize: number,
) => {
  const [{ records }, refetch] = useRefetchableFragment(
    RecordsFragment,
    datasetVersion,
  );
  const columns = React.useMemo(() => createSchemaColumns(schema), [schema]);
  const data = React.useMemo(() => Object.assign([], records), [records]);

  useOnChangeEffect(page, (page) => {
    refetch(
      {
        limit: pageSize,
        offset: page * pageSize,
      },
      { fetchPolicy: "store-or-network" },
    );
  });

  return useReactTable({
    columns,
    data,
    rowCount,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    debugTable: import.meta.env.DEV,
  });
};

interface DatasetVersionTableSkeletonProps {
  schema: Schema;
  pageSize: number;
}

function DatasetVersionTableSkeleton({
  schema,
  pageSize,
}: DatasetVersionTableSkeletonProps) {
  return (
    <Table>
      <TableHeader>
        <TableRow>
          {schema.fields.map((field) => (
            <TableHead key={field.name}>
              <Column field={field} />
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {range(pageSize).map((index) => (
          <TableRow key={index}>
            {schema.fields.map((field) => (
              <TableCell key={field.name}>
                <Skeleton className="w-32 h-6" />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface DatasetVersionTableRecordsProps {
  records: PreloadedQuery<DatasetVersionTableRecordsFragmentPaginationQueryType>;
  schema: Schema;
  rowCount: number;
  page: number;
  pageSize: number;
}

function DatasetVersionTableRecords({
  records,
  schema,
  rowCount,
  page,
  pageSize,
}: DatasetVersionTableRecordsProps) {
  const { node: datasetVersion } =
    usePreloadedQuery<DatasetVersionTableRecordsFragmentPaginationQueryType>(
      RecordsPaginationQuery,
      records,
    );

  const table = useDatasetVersionFileTable(
    datasetVersion,
    schema,
    rowCount,
    page,
    pageSize,
  );
  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              return (
                <TableHead key={header.id}>
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.map((row) => (
          <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
            {row.getVisibleCells().map((cell) => (
              <TableCell key={cell.id}>
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

export interface DatasetVersionTableProps {
  datasetVersion: DatasetVersionTableFragment$key;
  className?: string;
}

export const DatasetVersionTable = ({
  datasetVersion: datasetVersionFragment,
  className,
}: DatasetVersionTableProps) => {
  const isDesktop = useMediaQuery(DESKTOP_MEDIA_QUERY);
  const { page, setPage } = useDatasetVersionTableContext();
  const [query, loadQuery, disposeQuery] =
    useQueryLoader<DatasetVersionTableRecordsFragmentPaginationQueryType>(
      RecordsPaginationQuery,
    );

  const data = useFragment(Fragment, datasetVersionFragment);

  React.useEffect(() => {
    if (!data?.id || query) return;
    loadQuery(
      {
        id: data.id,
        limit: DEFAULT_PAGE_SIZE,
        offset: page * DEFAULT_PAGE_SIZE,
      },
      { fetchPolicy: "store-or-network" },
    );
    return disposeQuery;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disposeQuery]);

  const { schema, rows } = data;
  return (
    <div className={className}>
      <React.Suspense
        fallback={
          <DatasetVersionTableSkeleton
            schema={schema}
            pageSize={DEFAULT_PAGE_SIZE}
          />
        }
      >
        {query && (
          <DatasetVersionTableRecords
            records={query}
            schema={schema}
            rowCount={rows}
            page={page}
            pageSize={DEFAULT_PAGE_SIZE}
          />
        )}
      </React.Suspense>

      <div className="py-2">
        <AutoPagination
          maxDisplay={isDesktop ? 7 : 4}
          pageIndex={page}
          pageCount={Math.ceil(rows / DEFAULT_PAGE_SIZE)}
          setPageIndex={(pageIndex) => {
            setPage(pageIndex);
          }}
        />
      </div>
    </div>
  );
};
